import { ReactIntegration } from "@grafana/faro-react"
import { type Faro, getWebInstrumentations, initializeFaro, LogLevel } from "@grafana/faro-web-sdk"

import { Environment, resolveEnvironment } from "./env"

const FARO_COLLECT_URL = "https://faro.svc.instacover.ai/collect"

let faro: Faro | undefined

/**
 * Faro's default serializer runs String() over every argument, so `logger.info("msg", { ... })`
 * arrives in Loki as "msg [object Object]". Objects are stringified to keep the payload readable,
 * Errors are left to String() because JSON.stringify(new Error("x")) returns "{}".
 */
function logArgsSerializer(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error || typeof arg !== "object" || arg === null) {
        return String(arg)
      }

      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(" ")
}

/** The initialized Faro instance, or undefined outside deployed environments. */
export function getFaro(): Faro | undefined {
  return faro
}

/**
 * Grafana Faro — FE logs and errors into self-hosted Loki via the Alloy faro.receiver.
 * Runs in parallel with Sentry, same as the InstaCar setup it is ported from. Traces are out of
 * scope, which is why there is deliberately no TracingInstrumentation here.
 */
export function initFaro() {
  const environment = resolveEnvironment()

  // Deployed environments only, never localhost or an unknown host.
  if (environment === Environment.Unknown) {
    return
  }

  faro = initializeFaro({
    url: FARO_COLLECT_URL,
    app: {
      name: "instahome",
      version: process.env.SENTRY_RELEASE ?? "not-set",
      environment,
    },
    instrumentations: [
      // Errors, unhandled rejections, web-vitals, console capture.
      ...getWebInstrumentations({ captureConsole: true }),
      new ReactIntegration(),
    ],
    // console.debug/trace/log is not collected (Faro default), info and above is.
    consoleInstrumentation: {
      disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG],
    },
    logArgsSerializer,
  })

  faro.api.setSession({ attributes: { component: "instahome", subcomponent: "client" } })
}

type RegisterFaroSessionParams = {
  sessionId: string
  customerId: string
  customerName: string
  organizationId?: string
}

/**
 * Registers the business sessionId (plus customer and organization) as Faro session attributes so FE
 * logs in Loki can be correlated with server and worker logs. Surfaces as session_attr_sessionId.
 */
export function registerFaroSession(params: RegisterFaroSessionParams) {
  const { sessionId, customerId, customerName, organizationId } = params

  faro?.api.setSession({
    attributes: {
      sessionId,
      customerId,
      customerName,
      ...(organizationId === undefined ? {} : { organizationId }),
      component: "instahome",
      subcomponent: "client",
    },
  })
}
